import { useCallback, useState } from 'react';

import type { Product } from '../types/products.types';
import { productsApi } from '../api/products.api';

const asStorePrices = (value: unknown): Array<{ storeId: string; price: number }> => {
  if (!Array.isArray(value)) return [];
  return value
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const record = row as Record<string, unknown>;
      const storeId = String(record.storeId || record.store_id || '').trim();
      const price = Number(record.price);
      if (!storeId || !Number.isFinite(price)) return null;
      return { storeId, price };
    })
    .filter((row): row is { storeId: string; price: number } => Boolean(row));
};

const normalizeLiveProduct = (raw: unknown): Product | null => {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = String(row.id || row._id || row.frontendId || '').trim();
  if (!id) return null;
  const imageUrl = String(row.imageUrl || row.image || row.thumbnailUrl || '').trim();
  const storePrices = asStorePrices(row.storePrices);
  // Live API attaches open-now SoT via priceOpenAware. Never re-min storePrices
  // client-side — that ignored hours and showed grocery ambient overnight.
  const priceOpenAware = row.priceOpenAware === true;
  const mappedPrices = storePrices.map((sp) => Number(sp.price)).filter((price) => Number.isFinite(price) && price > 0);
  const catalogPrice = priceOpenAware
    ? Number(row.price) || 0
    : mappedPrices.length
      ? Math.min(...mappedPrices)
      : Number(row.price) || 0;
  const category = String(row.category || 'General').toLowerCase();
  const isBeverage = category === 'beverages' || category === 'drinks' || category.includes('beverage') || category.includes('drink');
  const itemName = String(row.name || '').toLowerCase();
  const isExplicitExemptBeverage =
    /\b(pure|purified|spring|drinking|distilled|alkaline|natural)\s+water\b/i.test(itemName) ||
    /\b(milk|creamer|juice|punch|lemonade|limeade|nectar|smoothie|shake|protein)\b/i.test(itemName) ||
    /\b(gatorade|powerade|bodyarmor|electrolit|vitaminwater)\b/i.test(itemName) ||
    (/\bwater\b/i.test(itemName) && !/\b(sparkling|carbonated|seltzer|fizzy|tonic)\b/i.test(itemName)) ||
    (/\btea\b/i.test(itemName) && !/\b(sparkling|carbonated)\b/i.test(itemName));
  const isCarbonatedBeverage =
    /\b(soda|pop|cola|coke|pepsi|sprite|dew|dr\s*pepper|faygo|vernors|ginger\s*ale|root\s*beer|7up|7-up|crush|fanta|squirt|tonic|seltzer|sparkling|carbonated|fizzy|club\s*soda)\b/i.test(itemName) ||
    /\b(beer|ale|lager|malt|hard\s*seltzer|cider|white\s*claw|truly)\b/i.test(itemName) ||
    /\b(monster|red\s*bull|celsius|reign|rockstar|ghost|c4|alani)\b/i.test(itemName);

  const hasBottleDeposit =
    typeof row.hasBottleDeposit === 'boolean'
      ? row.hasBottleDeposit
      : isBeverage && isCarbonatedBeverage && !isExplicitExemptBeverage;
  const packQuantityRaw = Math.floor(Number(row.packQuantity ?? row.pack_quantity ?? 0));
  const packFromName = (() => {
    const hay = `${row.name || ''} ${row.sizeLabel || ''}`.toLowerCase();
    const m = hay.match(/(\d+)\s*[-]?\s*(?:pack|pk|ct|count)\b/) || hay.match(/\b(\d+)\s*pk\b/);
    const n = m ? Math.floor(Number(m[1])) : 0;
    return Number.isFinite(n) && n >= 1 && n <= 48 ? n : 1;
  })();
  const packQuantity = packQuantityRaw >= 1 ? packQuantityRaw : packFromName;
  const depositPerUnit =
    typeof row.deposit === 'number' && row.deposit > 0
      ? row.deposit
      : hasBottleDeposit
        ? 0.1 * packQuantity
        : 0;

  return {
    ...(row as unknown as Product),
    id,
    frontendId: String(row.frontendId || id),
    name: String(row.name || 'Product'),
    brand: row.brand != null ? String(row.brand) : undefined,
    price: catalogPrice,
    priceOpenAware,
    pricingSource: row.pricingSource != null ? String(row.pricingSource) : undefined,
    pricingStoreId: row.pricingStoreId != null ? String(row.pricingStoreId) : undefined,
    fulfillmentStatus: row.fulfillmentStatus != null ? String(row.fulfillmentStatus) : undefined,
    availableForImmediateAdd:
      typeof row.availableForImmediateAdd === 'boolean' ? row.availableForImmediateAdd : undefined,
    availableForScheduledAdd:
      typeof row.availableForScheduledAdd === 'boolean' ? row.availableForScheduledAdd : undefined,
    nextOpen: (row as { nextOpen?: Product['nextOpen'] }).nextOpen ?? null,
    addBlockedReason:
      row.addBlockedReason == null ? null : String(row.addBlockedReason),
    deposit: depositPerUnit,
    packQuantity,
    stock: Number(row.stock) || 0,
    sizeOz: Number(row.sizeOz) || 0,
    sizeLabel: String(row.sizeLabel || '').trim() || undefined,
    sizeUnit: row.sizeUnit != null ? (String(row.sizeUnit) as Product['sizeUnit']) : undefined,
    description: String(row.description || '').trim() || undefined,
    nutritionSummary: String(row.nutritionSummary || '').trim() || undefined,
    nutritionNote: String(row.nutritionNote || '').trim() || undefined,
    nutritionImageUrl: String(row.nutritionImageUrl || '').trim() || undefined,
    category,
    image: imageUrl,
    imageUrl,
    isGlass: Boolean(row.isGlass),
    isCottageFood: Boolean(row.isCottageFood),
    cottageOperationName: row.cottageOperationName != null ? String(row.cottageOperationName) : '',
    cottageOperationAddress: row.cottageOperationAddress != null ? String(row.cottageOperationAddress) : '',
    allergens: Array.isArray(row.allergens) ? (row.allergens as string[]) : [],
    dietaryTags: Array.isArray(row.dietaryTags) ? (row.dietaryTags as string[]) : [],
    requiresAgeVerification: Boolean(row.requiresAgeVerification || row.isAgeRestricted),
    isAgeRestricted: Boolean(row.isAgeRestricted || row.requiresAgeVerification),
    minAge: typeof row.minAge === 'number' ? row.minAge : undefined,
    storePrices
  };
};

export const useProductsCore = (addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void) => {
  const [products, setProducts] = useState<Product[]>([]);

  const normalizeProductsPayload = (payload: unknown): Product[] => {
    let source: unknown[] = [];
    if (Array.isArray(payload)) source = payload;
    else if (payload && typeof payload === 'object') {
      const record = payload as Record<string, unknown>;
      if (Array.isArray(record.products)) source = record.products;
      else if (Array.isArray(record.data)) source = record.data;
      else if (record.data && typeof record.data === 'object' && Array.isArray((record.data as Record<string, unknown>).products)) {
        source = (record.data as Record<string, unknown>).products as unknown[];
      }
    }
    return source.map(normalizeLiveProduct).filter((row): row is Product => Boolean(row));
  };
  const fetchProducts = useCallback(async () => {
    try {
      const data = await productsApi.listLive();
      const list = normalizeProductsPayload(data);
      setProducts(list);
      return list;
    } catch (e: unknown) {
      setProducts([]);
      const msg = e instanceof Error ? e.message : String(e ?? 'Failed to load products');
      addToast(msg, 'warning');
      return [];
    }
  }, [addToast]);

  const createProduct = useCallback(async (p: Partial<Product>) => {
    try {
      const data = await productsApi.create({
        id: p.id,
        name: p.name,
        price: p.price,
        deposit: p.deposit ?? 0,
        stock: p.stock ?? 0,
        sizeOz: p.sizeOz ?? 0,
        category: p.category ?? 'DRINK',
        image: p.image ?? '',
        isGlass: p.isGlass ?? false,
        isCottageFood: p.isCottageFood ?? false,
        cottageOperationName: p.cottageOperationName || '',
        cottageOperationAddress: p.cottageOperationAddress || '',
        allergens: Array.isArray(p.allergens) ? p.allergens : [],
        upc: p.upc
      } as Partial<Product> as Record<string, unknown>);

      const created = data.product as Product;
      setProducts(prev => [created, ...prev]);
      addToast('PRODUCT CREATED', 'success');
      return created;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e ?? 'Failed to create product');
      addToast(msg, 'error');
      throw e;
    }
  }, [addToast]);

  const updateProduct = useCallback(async (id: string, updates: Partial<Product>) => {
    try {
      const data = await productsApi.update(id, updates as Record<string, unknown>);
      const updated = data.product as Product;
      setProducts(prev => prev.map(p => (p.id === id ? { ...p, ...updated } : p)));
      return updated;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e ?? 'Failed to update product');
      addToast(msg, 'error');
      throw e;
    }
  }, [addToast]);

  const deleteProduct = useCallback(async (id: string) => {
    try {
      await productsApi.remove(id);
      setProducts(prev => prev.filter(p => p.id !== id));
      addToast('PRODUCT REMOVED', 'success');
      return true;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e ?? 'Failed to delete product');
      addToast(msg, 'error');
      throw e;
    }
  }, [addToast]);

  return { products, setProducts, fetchProducts, createProduct, updateProduct, deleteProduct };
};
